import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import { CalendarClock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';
import { Button } from './button.js';
import { Calendar } from './calendar.js';
import { pickerTriggerClassName } from './date-picker.js';
import { Input } from './input.js';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';

/** `14:05` — the value an `<input type="time">` reads and writes. */
function toTimeValue(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Applies `14:05` to a day, leaving the day itself alone. */
function withTime(day: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number);
  const next = new Date(day);
  next.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return next;
}

export interface DateTimePickerLabels {
  /** Names the time field — "Time". */
  time: string;
  /** Closes the popover — "Done". */
  done: string;
}

export interface DateTimePickerProps {
  value?: Date;
  onChange?: (value: Date | undefined) => void;
  placeholder?: string;
  labels: DateTimePickerLabels;
  /** Blocks days before today. A snooze into the past wakes immediately. */
  disablePast?: boolean;
  /**
   * The hour and minute a freshly picked day lands on, before the time
   * field is touched. 09:00 rather than midnight: "tomorrow" means the
   * start of the working day, and a thread that wakes at 00:00 waits eight
   * hours on a desk nobody is sitting at.
   */
  defaultTime?: string;
  /** Minutes the native stepper moves by. */
  minuteStep?: number;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/**
 * A day *and* a time of day.
 *
 * `DatePicker` is date-granularity, which is right for a hire window and
 * wrong for a snooze: `PATCH /v1/threads/:id` refuses `SNOOZED` without a
 * `snoozedUntil`, and "wake me on Thursday" is not an instruction anybody
 * means — they mean Thursday morning.
 *
 * Built out of the same `Calendar` and `Popover` as `DatePicker`, wearing
 * `pickerTriggerClassName`, rather than a second calendar. The time half is
 * a native `<input type="time">` on purpose: it is the one control that
 * already knows whether this reader writes 14:05 or 2:05 PM, and it carries
 * a keyboard and a screen reader for free.
 *
 * Both halves land in one `onChange` — the caller gets a whole instant or
 * nothing, never a date waiting for a time that never came.
 */
export function DateTimePicker({
  value,
  onChange,
  placeholder,
  labels,
  disablePast = true,
  defaultTime = '09:00',
  minuteStep = 5,
  disabled,
  className,
  id,
}: DateTimePickerProps) {
  const { i18n } = useTranslation('common');
  const locale = toLocale(i18n.language);
  const [open, setOpen] = useState(false);

  const shown = value ? formatDateTimeIn(value, locale) : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(pickerTriggerClassName, !value && 'text-text-subtle', className)}
        >
          <CalendarClock className="opacity-60" />
          {shown}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-2">
        <Calendar
          mode="single"
          autoFocus
          selected={value}
          defaultMonth={value}
          onSelect={(day) => {
            // The popover stays open, unlike `DatePicker`'s: half the
            // answer is still in the row underneath.
            if (!day) return onChange?.(undefined);
            onChange?.(withTime(day, value ? toTimeValue(value) : defaultTime));
          }}
          disabled={disablePast ? { before: new Date() } : undefined}
        />

        <div className="mt-1 flex items-center gap-2 border-t border-border-default px-1 pt-2">
          <Input
            type="time"
            // The attribute counts seconds, the prop counts minutes.
            step={minuteStep * 60}
            aria-label={labels.time}
            value={value ? toTimeValue(value) : defaultTime}
            // A time with no day is not an instant, so the field waits for
            // the calendar rather than inventing today.
            disabled={!value}
            onChange={(event) => {
              if (!value || !event.target.value) return;
              onChange?.(withTime(value, event.target.value));
            }}
            className="h-9 w-32"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={() => setOpen(false)}
          >
            {labels.done}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
